



























































import { CLASSIFIED_SEARCH_NS } from '~/store/modules/shared/classifieds/search/state'
import { debounce } from '~/utils/function'
import { MakeLogoType } from '~/models/make'
import { computed, defineComponent, ref, watch } from '~/utils/nuxt3-migration'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import CMakeLogo from '~/components/shared/configurable/make/CMakeLogo.vue'
import { useNamespacedStore } from '~/compositions/store'
import useClassifiedSearchFacet from '~/compositions/classified-search/useClassifiedSearchFacet'
import { useI18n } from '~/compositions/i18n'
import { Searchable } from '~/models/search/types'
import CClassifiedMakeCustomSelect from '~/components/shared/configurable/classified/CClassifiedMakeCustomSelect.vue'
import { facetHandlerComponentProps } from '~/constants/classified/search/facet'
import { Option } from '~/models/shared/types'

interface ModelOption extends Option {
  belongsToASeries?: boolean
}

export default defineComponent({
  components: {
    CClassifiedMakeCustomSelect,
    CCustomSelect,
    CMakeLogo
  },
  props: facetHandlerComponentProps,
  setup(props) {
    const {
      state: classifiedSearchState,
      getters: classifiedSearchGetters
    } = useNamespacedStore(CLASSIFIED_SEARCH_NS)
    const { t } = useI18n()

    const makesSelectRef = ref<typeof CCustomSelect>()
    const selectedMakesValues = ref<number[]>(props.selected.makes || [])
    const selectedModelsValues = ref<number[]>(props.selected.models || [])
    const modelsSelectRef = ref<typeof CCustomSelect>()
    const variantText = ref(props.selected.variant || '')

    const { changeParam, changeParams } = useClassifiedSearchFacet({
      onClearAll: () => {
        if (modelsSelectRef.value) {
          modelsSelectRef.value.clearSearchInput()
        }

        if (makesSelectRef.value) {
          makesSelectRef.value.clearSearchInput()
        }
      }
    })

    watch(
      () => props.selected,
      (newValue: { makes: number[]; models: number[]; variant: string }) => {
        selectedMakesValues.value = newValue.makes || []
        selectedModelsValues.value = newValue.models || []
        variantText.value = newValue.variant || ''
      }
    )

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const inCarsSearch = computed(() => classifiedSearchGetters('inCarsSearch'))

    const inBikesSearch = computed(() =>
      classifiedSearchGetters('inBikesSearch')
    )

    const inDealerSiteSearch = computed(() =>
      classifiedSearchGetters('inDealerSiteSearch')
    )

    const makeLogoType = computed(() =>
      inBikesSearch.value ? MakeLogoType.BIKES : MakeLogoType.CARS
    )

    const makeSearchables = computed(() => {
      if (!props.searchables) {
        return []
      }

      return inDealerSiteSearch.value
        ? props.searchables.filter((s: Searchable) => s.c > 0)
        : props.searchables
    })

    const showTopMakesAsButtons = computed(
      () => inCarsSearch.value || inBikesSearch.value
    )

    const showMakesLogos = computed(
      () => inCarsSearch.value || inBikesSearch.value
    )

    const selectedMakesSearchables = computed(() =>
      makeSearchables.value.filter((searchable: Searchable) => searchable.s)
    )

    const modelOptionGroups = computed(() =>
      selectedMakesSearchables.value
        .filter((make: Searchable) => make.k?.length)
        .map((make: Searchable) => ({
          label: make.n,
          options: (make.k || []).reduce(
            (acc: ModelOption[], model: Searchable) => {
              // Add the main model option
              acc.push({
                text: model.n,
                value: model.v,
                count: model.c
              })

              // Add series options if they exist
              if (model.k?.length) {
                const seriesOptions = model.k.map(
                  (seriesModel: Searchable): ModelOption => ({
                    text: seriesModel.n,
                    value: seriesModel.v,
                    count: seriesModel.c,
                    belongsToASeries: true
                  })
                )
                acc.push(...seriesOptions)
              }

              return acc
            },
            []
          )
        }))
    )

    const modelsSelectPlaceholder = computed(() => {
      if (!selectedMakesValues.value.length) {
        return t('model')
      }

      return modelOptionGroups.value.length ? t('model') : t('no models found')
    })

    const makesOptions = computed(() =>
      makeSearchables.value.map((s: Searchable) => ({
        text: s.n,
        value: s.v,
        count: s.c,
        isTop: s.is_top
      }))
    )

    const onVariantInput = debounce((text: string) => {
      changeParam({
        name: 'variant',
        value: text
      })
    })

    const topMakesOptions = computed(() =>
      makeSearchables.value
        .filter((s: Searchable) => s.is_top)
        .map((s: Searchable) => ({
          text: s.n,
          value: s.v,
          count: s.c,
          isTop: s.is_top
        }))
    )

    function onClearButtonClick() {
      selectedMakesValues.value = []
      selectedModelsValues.value = []
      variantText.value = ''

      changeParams([
        { name: 'make', value: null },
        { name: 'model', value: null },
        { name: 'variant', value: null }
      ])
    }

    function onMakesChange(makesIds: number[]) {
      if (!makesIds.length) {
        // No makes are selected, so we can clear all makes and models
        changeParams([
          { name: 'make', value: null },
          { name: 'model', value: null }
        ])

        return
      }

      if (!selectedModelsValues.value.length) {
        // No models are selected, so we can just set the makes
        changeParam({
          name: 'make',
          value: makesIds
        })

        return
      }

      // Some models are already selected, so we need to handle the make child models deselection
      const modelsToKeep = selectedModelsValues.value.filter(
        (modelId: number) =>
          makesIds.some(makeId => {
            const makeSearchable = makeSearchables.value.find(
              (s: Searchable) => s.v === makeId
            )

            // Check if the make has children and if the modelId is one of them
            return (
              makeSearchable?.children?.some(
                (child: Searchable) => child.v === modelId
              ) ?? false
            )
          })
      )

      return changeParams([
        { name: 'make', value: makesIds },
        { name: 'model', value: modelsToKeep }
      ])
    }

    function onModelsChange(modelsIds: number[]) {
      changeParam({
        name: 'model',
        value: modelsIds
      })
    }

    return {
      showMakesLogos,
      selectedMakesValues,
      selectedModelsValues,
      makeLogoType,
      makeSearchables,
      makesSelectRef,
      modelsSelectRef,
      modelOptionGroups,
      variantText,
      loadingFacets,
      selectedMakesSearchables,
      modelsSelectPlaceholder,
      onVariantInput,
      makesOptions,
      topMakesOptions,
      showTopMakesAsButtons,
      onClearButtonClick,
      onMakesChange,
      onModelsChange
    }
  }
})
